import {
  FBN_LOOKBACK_DAYS,
  FBN_ORIGIN,
  HDL_BROWSER_HOSTS,
  OCHCA_PLAN_REVIEW_URL,
} from '../../../shared/fields';
import type { FbnDetailRecord, FbnRowRecord, PlanCheckRecord } from '../../../shared/fields';
import { shell } from '../app';
import { NetworkError } from '../data/api';
import { importCityLicenses, importFbnDetails, importFbnRows, importPlanChecks } from '../data/signals';
import {
  FBN_WANTED_MESSAGE,
  HDL_ORIGINS,
  PORTAL_ORIGIN,
  READY_MESSAGE,
  acceptMessage,
} from '../logic/bookmarklet';
import type { hdlSourceOf } from '../logic/bookmarklet';
import { SOURCE_NAMES } from '../logic/signals';
import { btn, empty, linkBtn } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import { COUNTY_BACK, countyConfirmStep, countyResultStep, plural } from './importCounty';
import type { View } from '../ui/router';

const PING_MS = 500;
/** Ten minutes: the county read runs seven days of searches in the rep's browser (a minute or two live);
 *  the portal and a city answer within seconds. A closed page ends the wait at once. */
const MAX_PINGS = 1200;
const CLOSED = 'The page was closed. Click "Send to CRM" on the OC Health, city or county page again.';

const inboxButton = () =>
  linkBtn('Open New businesses', '#/signals', { variant: 'primary', size: 'lg', class: 'w-full' });

/** Plan checks that arrived from the portal window, waiting for the rep's go: the portal is a shared
 *  vendor site, so nothing is saved without a tap (D58). */
const confirmStep = (records: PlanCheckRecord[], onImport: () => void): HTMLElement[] => [
  h(
    'div',
    { class: 'section' },
    h('h2', { class: 'section__title' }, `${records.length} plan checks received`),
    h(
      'p',
      { class: 'card__summary' },
      'From the OC Health page you just used: food plan checks of the last 60 days.',
    ),
  ),
  btn(`Import ${records.length} plan checks`, {
    variant: 'primary',
    size: 'lg',
    class: 'w-full',
    onClick: onImport,
  }),
];

/** A city's licenses that arrived from its HdL page (D62), waiting for the rep's go like plan checks. */
const cityConfirmStep = (name: string, licenses: number, onImport: () => void): HTMLElement[] => [
  h(
    'div',
    { class: 'section' },
    h('h2', { class: 'section__title' }, `${licenses} licenses from ${name}`),
    h(
      'p',
      { class: 'card__summary' },
      `From ${name}'s license search: every license started in the last 45 days.`,
    ),
  ),
  btn(`Import ${name}'s licenses`, { variant: 'primary', size: 'lg', class: 'w-full', onClick: onImport }),
];

const cityResultStep = (r: {
  received: number;
  kept: number;
  inserted: number;
  pending?: number;
}): HTMLElement[] => [
  h(
    'div',
    { class: 'section' },
    h('h2', { class: 'section__title' }, r.inserted ? `${r.inserted} new to review` : 'Nothing new yet'),
    h(
      'p',
      { class: 'card__summary' },
      [
        `${r.received} licenses · ${r.kept} in town with a street address · ${r.inserted} storefronts not seen before`,
        r.pending ? `${r.pending} more Google will tell about tomorrow morning` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    ),
  ),
  inboxButton(),
];

const resultStep = (r: { received: number; kept: number; inserted: number }): HTMLElement[] => [
  h(
    'div',
    { class: 'section' },
    h('h2', { class: 'section__title' }, r.inserted ? `${r.inserted} new to review` : 'Nothing new'),
    h(
      'p',
      { class: 'card__summary' },
      `${r.received} plan checks · ${r.kept} new builds, remodels, food trucks and markets · ${r.inserted} not seen before`,
    ),
  ),
  inboxButton(),
];

const failedStep = (
  err: unknown,
  back = { href: OCHCA_PLAN_REVIEW_URL, label: 'Back to OC Health' },
): HTMLElement[] => [
  empty(
    err instanceof NetworkError ? 'Importing needs a connection.' : 'Could not import.',
    err instanceof Error ? err.message : undefined,
  ),
  h('a', { class: 'cardlink', href: back.href, target: '_blank', rel: 'noopener' }, back.label),
];

/** Where the "Send to CRM" bookmark lands (D58, D62, D76): this tab was opened by the OC Health page, a
 *  city's HdL page or the county's search, which hands over what it read once this screen says it is
 *  ready. Only that window, from that origin, is heard. The county takes two hand-offs: its rows, then
 *  the pages of the filings this screen asked it to open. */
export const importPlanChecksView: View = ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Plan checks', { back: '#/signals' });
  const host = h('div', { class: 'page page--import' });
  root.appendChild(host);
  const show = (...children: HTMLElement[]) => replaceChildren(host, ...children);

  const portal = window.opener as Window | null;
  if (!portal) {
    show(
      empty(
        'Open this from the OC Health page.',
        'Click "Send to CRM" on the plan-review page. Set it up from New businesses.',
      ),
      inboxButton(),
    );
    return;
  }
  show(h('p', { class: 'card__summary' }, 'Waiting for the page…'));

  let pings = 0;
  const gaveUp = (why: string, hint: string) => {
    clearInterval(ping);
    show(empty(why, hint), inboxButton());
  };
  const ping = setInterval(() => {
    if (portal.closed) return gaveUp(CLOSED, 'Nothing was imported.');
    // the opener is the OC Health portal, a city's HdL site or the county's search: a message to any
    // other origin is dropped
    if (++pings <= MAX_PINGS)
      return [PORTAL_ORIGIN, ...HDL_ORIGINS, FBN_ORIGIN].forEach((origin) =>
        portal.postMessage(READY_MESSAGE, origin),
      );
    gaveUp('The page did not answer.', 'Click "Send to CRM" on the OC Health, city or county page again.');
  }, PING_MS);

  const save = async (records: PlanCheckRecord[]) => {
    show(h('p', { class: 'card__summary' }, `Importing ${records.length} plan checks…`));
    try {
      show(...resultStep(await importPlanChecks(records)));
    } catch (err) {
      show(...failedStep(err));
    }
  };

  const saveCity = async (source: NonNullable<ReturnType<typeof hdlSourceOf>>, rows: string[][]) => {
    show(h('p', { class: 'card__summary' }, `Importing ${SOURCE_NAMES[source]}'s licenses…`));
    try {
      show(...cityResultStep(await importCityLicenses(source, rows)));
    } catch (err) {
      show(
        ...failedStep(err, {
          href: `${HDL_BROWSER_HOSTS[source]}/Search/Index/BusinessLicense`,
          label: `Back to ${SOURCE_NAMES[source]}'s license search`,
        }),
      );
    }
  };

  let county: { days: string[]; rows: FbnRowRecord[] } | null = null;
  let watch: ReturnType<typeof setInterval> | null = null;
  const countyRows = async (days: string[], rows: FbnRowRecord[]) => {
    show(h('p', { class: 'card__summary' }, `Checking ${plural(rows.length, 'filing')} from the county…`));
    try {
      const { wanted } = await importFbnRows(rows, days);
      if (wanted.length === 0) {
        stop();
        show(
          ...countyResultStep(
            { received: rows.length, kept: 0, inserted: 0, pending: 0 },
            `${plural(rows.length, 'filing')} in the last ${FBN_LOOKBACK_DAYS} days: none worth opening, or all seen before.`,
          ),
        );
        return;
      }
      portal.postMessage({ type: FBN_WANTED_MESSAGE, files: wanted }, FBN_ORIGIN);
      show(
        h(
          'p',
          { class: 'card__summary' },
          `Opening ${plural(wanted.length, 'filing')} on the county's site…`,
        ),
      );
      // the county tab may be closed before it answers: the wait ends with a word, not a spinner
      watch = setInterval(() => {
        if (!portal.closed) return;
        stop();
        show(...failedStep(new Error(CLOSED), COUNTY_BACK));
      }, 1000);
    } catch (err) {
      stop();
      show(...failedStep(err, COUNTY_BACK));
    }
  };
  const saveCounty = async (days: string[], rows: FbnRowRecord[], details: FbnDetailRecord[]) => {
    show(h('p', { class: 'card__summary' }, `Checking ${plural(details.length, 'filing')} with Google…`));
    try {
      show(...countyResultStep(await importFbnDetails(rows, days, details)));
    } catch (err) {
      show(...failedStep(err, COUNTY_BACK));
    }
  };

  // only the window that opened this tab, and only from the origin each message is for; the county's
  // second hand-off (the details) keeps the listener until it comes
  const onMessage = (e: MessageEvent) => {
    const got = acceptMessage(e, portal);
    if (!got) return;
    clearInterval(ping);
    if (got.kind === 'plan-checks') {
      stop();
      show(...confirmStep(got.records, () => void save(got.records)));
    } else if (got.kind === 'city') {
      stop();
      show(
        ...cityConfirmStep(
          SOURCE_NAMES[got.source],
          Math.max(got.rows.length - 1, 0),
          () => void saveCity(got.source, got.rows),
        ),
      );
    } else if (got.kind === 'fbn-rows') {
      county = { days: got.days, rows: got.rows };
      void countyRows(got.days, got.rows);
    } else if (county) {
      const { days, rows } = county;
      stop();
      if (got.error && got.details.length === 0) show(...failedStep(new Error(got.error), COUNTY_BACK));
      else
        show(
          ...countyConfirmStep(
            got.details.length,
            () => void saveCounty(days, rows, got.details),
            got.error ? `The rest could not be opened: ${got.error}` : undefined,
          ),
        );
    }
  };
  const stop = () => {
    clearInterval(ping);
    if (watch) clearInterval(watch);
    removeEventListener('message', onMessage);
  };
  addEventListener('message', onMessage);

  return () => {
    stop();
  };
};
